import { z } from 'zod';
import { baseLevelSchema } from './levels';
import { mod_menus } from './modmenus';
import { BaseUser, extendedBaseUserSchema } from './users';
import { PaginatedResponse } from './api';

export const submissionStatus = [
    'Pending',
    'Claimed',
    'UnderConsideration',
    'Denied',
    'Accepted',
    'UnderReview',
] as const;

export type SubmissionStatus = (typeof submissionStatus)[number];

export const submissionSchema = z.object({
    id: z.string(),
    level: baseLevelSchema,
    submitted_by: extendedBaseUserSchema,
    video_url: z.url(),
    completion_time: z.number().min(0).optional(),
    mobile: z.boolean(),
    mod_menu: z.enum(mod_menus).nullable(),
    custom_copy_id: z.number().nullable(),
    raw_url: z.url().nullable(),
    user_notes: z.string().max(300).nullable(),
    reviewer_notes: z.string().max(1000).nullable(),
    private_reviewer_notes: z.string().max(1000).nullable(),
    status: z.enum(submissionStatus).default('Pending'),
    reviewer: extendedBaseUserSchema.nullable(),
    locked: z.boolean(),
    created_at: z.string(),
    updated_at: z.string(),
});

export type ResolvedSubmission = z.infer<typeof submissionSchema>;

export type UnresolvedSubmission = Omit<
    ResolvedSubmission,
    'level' | 'reviewer' | 'submitted_by' | 'list'
> & {
    level: string;
    reviewer: string | null;
    submitted_by: string;
};

export const recordSchema = submissionSchema
    .pick({
        id: true,
        level: true,
        mobile: true,
        submitted_by: true,
        video_url: true,
        completion_time: true,
        created_at: true,
        updated_at: true,
    })
    .extend({
        submission_id: z.string(),
        is_verification: z.boolean().default(false),
        hide_video: z.boolean().default(false),
        achieved_at: z.string(),
    });

export type ResolvedRecord = z.infer<typeof recordSchema>;

export type LevelResolvedRecord = Omit<ResolvedRecord, 'level'>;
export type ProfileRecord = Omit<ResolvedRecord, 'submitted_by'>;

export type SubmissionOrRecord = ResolvedSubmission | ResolvedRecord;

export type SubmissionsResponse = PaginatedResponse<ResolvedSubmission>;

export type RecordResponse = PaginatedResponse<ResolvedRecord>;

export interface QueuePosition {
    position: number;
    priority: boolean;
}

export interface QueueTotal {
    regular_submissions_in_queue: number;
    regular_submissions_without_raw_in_queue: number;
    priority_submissions_in_queue: number;
    priority_submissions_without_raw_in_queue: number;
    uc_submissions: number;
    oldest_submission: string;
}

export interface SubmissionHistory extends Partial<
    Pick<
        ResolvedSubmission,
        | 'video_url'
        | 'mobile'
        | 'mod_menu'
        | 'completion_time'
        | 'custom_copy_id'
        | 'raw_url'
        | 'user_notes'
        | 'reviewer_notes'
        | 'private_reviewer_notes'
        | 'reviewer'
        | 'locked'
    >
> {
    id: string;
    submission_id: string;
    timestamp: string;
    status: SubmissionStatus;
}

export type CompletionTime = {
    hours: number;
    minutes: number;
    seconds: number;
    milliseconds: number;
};

export interface SubmissionsEnabledStatus {
    enabled: boolean;
    moderator: BaseUser;
    created_at: string;
}

export interface ProbeEntry<T> {
    data: T | null;
    error?: string;
}

export interface FFProbeStream {
    index: number;
    codec_name?: string;
    codec_type?: string;
    width?: number;
    r_frame_rate?: string;
    avg_frame_rate?: string;
    height?: number;
    channels?: number;
    sample_rate?: string;
    tags?: {
        handler_name?: string;
        title?: string;
        handler?: string;
    };
}

export interface FFProbeResult {
    format: object;
    streams: FFProbeStream[];
}

export interface MediaProbeResult {
    probed_url: string;
    exif: ProbeEntry<object[]>;
    ffprobe: ProbeEntry<FFProbeResult>;
}
